// include the pca.js types, in case we're importing the source
// from outside of the cg project

// tslint doesn't like <reference>
/* tslint:disable-next-line */
/// <reference path="./projection.d.ts" />

import * as _ from 'lodash';

import * as OpTypes from '../opTypes';
import * as OpKinds from '../opKinds';
import * as Graph from '../graph';
import * as HL from '../hl';
import {
  constNode,
  constNodeUnsafe,
  constNumber,
  constString,
  opAssetFile,
  opDict,
  opFileDigest,
} from '../ops';
import {replaceInputVariables} from '../refineHelpers';
import {escapeDots, opPick, splitEscapedString} from './typedDict';
import * as Types from '../model/types';
import {allObjPaths} from '../model/typeHelpers';
import {MAX_RUN_LIMIT} from '../utils/constants';
import {tSNE} from './tsne';

const listArg0 = {
  arr: {type: 'list' as const, objectType: 'any' as const},
};

const makeListOp = OpKinds.makeBasicOp;

const unwrapConcreteList = (l: any): any[] => {
  if (Types.isConcreteTaggedValue(l)) {
    const tag = OpTypes.getTagFromTaggedValue(l);
    const list = OpTypes.getValueFromTaggedValue(l);
    if (_.isArray(list) && tag != null) {
      return list.map(item => Types.concreteTaggedValue(tag, item));
    }
  }
  return l;
};

// Internally convert grouping/joining on assets to their digest
const toSafeCall = (call: Types.Node<Types.Type>) => {
  const callType = Types.nullableTaggableValue(call.type);
  if (Types.typesWithDigest.some(t => Types.isAssignableTo2(callType, t))) {
    return opFileDigest({file: opAssetFile({asset: call})});
    // TODO: what is this code for. Its slow.
  } else if (Types.isTypedDict(call.type)) {
    const newGroupedNode: {[key: string]: Types.Node} = {};
    for (const propertyType in call.type.propertyTypes) {
      if (call.type.propertyTypes[propertyType] != null) {
        newGroupedNode[propertyType] = toSafeCall(
          opPick({
            obj: call,
            key: constString(escapeDots(propertyType)),
          })
        );
      }
    }
    return opDict(newGroupedNode as any);
  } else {
    return call;
  }
};

// TODO: should this be mapped, and we make a different op called len
// that does this behavior?
// At the least, this should be called len or length.
export const opCount = makeListOp({
  name: 'count',
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
  },
  description: `Returns the count of elements in the ${Graph.docType('list')}.`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to count.`,
  },
  returnValueDescription: `The count of elements in the ${Graph.docType(
    'list'
  )}.`,
  returnType: inputsTypes => 'number',
  resolver: ({arr}) => arr.length,
});

export const opJoinToStr = makeListOp({
  name: 'joinToStr',
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
    sep: 'string',
  },
  description: `Joins the elements of the ${Graph.docType(
    'list'
  )} into a ${Graph.docType('string')}.`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to join.`,
    sep: 'The separator to use between elements.',
  },
  returnValueDescription: `The joined ${Graph.docType('string')}.`,
  returnType: inputsTypes => 'string',
  resolver: ({arr, sep}) => arr.join(sep),
});

// Not exposed, but we need some kind of mapped count for count of count.
export const opCountInner = Graph.makeOp({
  hidden: true,
  name: 'mapcount',
  argTypes: {
    arr: {type: 'list', objectType: {type: 'list', objectType: 'any'}},
  },
  description: `Returns the count of elements in each ${Graph.docType(
    'list'
  )} in a ${Graph.docType('list')} of ${Graph.docType('list')}.`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} of ${Graph.docType('list', {
      plural: true,
    })} to count.`,
  },
  returnValueDescription: `The count of elements in each ${Graph.docType(
    'list'
  )}`,
  returnType: {type: 'list', objectType: 'number'},
  resolver: inputs => {
    const {arr} = inputs;
    if (
      (_.isArray(arr) && _.isArray(arr[0])) ||
      (arr[0]?._value != null && _.isArray(arr[0]._value))
    ) {
      return arr.map((innerArr: any) => {
        if (innerArr._value != null) {
          return innerArr._value.length;
        }
        return innerArr.length;
      });
    }
    throw new Error('invalid');
  },
});

// TODO: This should produce some new type like undefined when indexing
// out of bounds. tagging undefined should produce undefined (it can't be
// tagged)
export const opIndex = makeListOp({
  name: 'index',
  description: `Retrieve a value from a ${Graph.docType('list')} by index`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to index into.`,
    index: 'The index to retrieve',
  },
  returnValueDescription: `A value from the ${Graph.docType('list')}`,
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
    index: 'number',
  },
  renderInfo: {
    type: 'brackets',
  },
  returnType: ({arr}) => Types.listObjectType(arr),
  resolver: ({arr, index}) => arr[index],
});

export const opOffset = makeListOp({
  hidden: true,
  name: 'offset',
  argTypes: {arr: {type: 'list', objectType: 'any'}, offset: 'number'},
  description: `Returns a ${Graph.docType(
    'list'
  )} with the first \`offset\` elements removed.`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to remove elements from.`,
    offset: 'The count of elements to remove.',
  },
  returnValueDescription: `The ${Graph.docType(
    'list'
  )} with the first \`offset\` elements removed.`,
  returnType: ({arr}) => arr,
  resolver: ({arr, offset}) => arr.slice(offset),
});

export const opLimit = makeListOp({
  hidden: true,
  name: 'limit',
  argTypes: {arr: {type: 'list', objectType: 'any'}, limit: 'number'},
  description: `Returns a ${Graph.docType(
    'list'
  )} of the first \`limit\` elements.`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to limit.`,
    limit: 'The count of elements to limit to.',
  },
  returnValueDescription: `The ${Graph.docType(
    'list'
  )} with the first \`limit\` elements.`,
  returnType: ({arr}, inputNodes) => {
    if (inputNodes.limit.nodeType === 'const') {
      return {
        ...arr,
        maxLength: inputNodes.limit.val,
      };
    }
    return arr;
  },
  resolver: ({arr, limit}) => arr.slice(0, limit),
});

// Exported for PanelFacet :( :(. TODO: fix
export const compareItems = (a: any, b: any): number => {
  if (_.isArray(a)) {
    for (let i = 0; i < a.length; i++) {
      const res = compareItems(a[i], b[i]);
      if (res !== 0) {
        return res;
      }
    }
  } else {
    if (a == null) {
      return -1;
    } else if (b == null) {
      return 1;
    } else if (a < b) {
      return -1;
    } else if (a > b) {
      return 1;
    }
  }
  return 0;
};

export const opSort = makeListOp({
  name: 'sort',
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
    compFn: {
      type: 'function',
      inputTypes: {row: 'any'},
      outputType: {type: 'list', objectType: 'any'},
    },
    columnDirs: {type: 'list', objectType: 'string'},
  },
  description: `Sorts the ${Graph.docType('list')}.`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to sort.`,
    compFn: `A function to apply to each element of the array. The return value is a ${Graph.docType(
      'list'
    )} of values to sort by.`,
    columnDirs: `A ${Graph.docType(
      'list'
    )} of directions for each element in the value ${Graph.docType('list')}`,
  },
  returnValueDescription: `The sorted ${Graph.docType('list')}.`,
  returnType: inputTypes => inputTypes.arr,
  resolver: async (
    inputs,
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context,
    engine
  ) => {
    const arrType = inputTypes.arr;
    const objectType = Types.listObjectType(arrType);
    const calls: Types.Node[] = unwrapConcreteList(rawInputs.arr).map(
      (x: any) =>
        HL.callFunction(inputs.compFn, {
          row: constNodeUnsafe(objectType, x),
        })
    );
    const comparableObjs = await engine().executeNodes(calls, true);
    return (inputs.arr as any[])
      .map((row, ndx) => {
        return {
          row,
          comparableObj: comparableObjs[ndx],
        };
      })
      .sort((rowA, rowB) => {
        for (let i = 0; i < inputs.columnDirs.length; i++) {
          const comp = compareItems(
            rowA.comparableObj[i],
            rowB.comparableObj[i]
          );
          if (comp !== 0) {
            return comp * (inputs.columnDirs[i] === 'asc' ? 1 : -1);
          }
        }
        return 0;
      })
      .map(item => {
        return item.row;
      });
  },
});

export const opFilter = makeListOp({
  name: 'filter',
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
    filterFn: {
      type: 'function',
      inputTypes: {row: 'any'},
      outputType: Types.maybe('boolean'),
    },
  },
  description: `Filters the ${Graph.docType('list')}.`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to filter.`,
    filterFn: `A function to apply to each element of the ${Graph.docType(
      'list'
    )}. The return value is a boolean indicating whether the element should be included in the result.`,
  },
  returnValueDescription: `The filtered ${Graph.docType('list')}.`,
  returnType: ({arr}) => arr,
  resolver: async (
    inputs,
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context,
    engine
  ) => {
    const arrType = inputTypes.arr;
    const objectType = Types.listObjectType(arrType);
    const {arr, filterFn} = inputs;
    const calls: Types.Node[] = unwrapConcreteList(rawInputs.arr).map(
      (x: any) =>
        HL.callFunction(filterFn, {row: constNodeUnsafe(objectType, x)})
    );

    const result: any[] = [];
    const predicateResult = await engine().executeNodes(calls, true);
    for (let i = 0; i < predicateResult.length; i++) {
      const predRes = predicateResult[i];
      const val = arr[i];
      if (predRes) {
        result.push(val);
      }
    }
    return result;
  },
});

export const opIndexCheckpoint = makeListOp({
  name: 'list-indexCheckpoint',
  hidden: true,
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
  },
  description: `Tags each item in the ${Graph.docType('list')} with its index`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to tag with indices`,
  },
  returnValueDescription: `The ${Graph.docType(
    'list'
  )} with each element tagged with its index`,
  returnType: ({arr}) => {
    return Types.list(
      Types.taggedValue(
        Types.typedDict({indexCheckpoint: 'number'}),
        arr.objectType
      ),
      Types.listMinLength(arr),
      Types.listMaxLength(arr)
    );
  },
  resolver: async ({arr}, inputTypes) => {
    return arr.map((x, i) =>
      Types.concreteTaggedValue({indexCheckpoint: i}, x)
    );
  },
});

export const opGetIndexCheckpointTag = OpKinds.makeTagGetterOp({
  name: 'tag-indexCheckpoint',
  tagName: 'indexCheckpoint',
  tagType: 'number',
  hidden: true,
});

export const opDropNa = OpKinds.makeConfigurableStandardOp({
  name: 'dropna',
  typeConfig: {dims: 1},
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
  },
  description: `Drops elements of a ${Graph.docType('list')} which are null`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to drop elements from.`,
  },
  returnValueDescription: `The ${Graph.docType(
    'list'
  )} with null elements removed.`,
  returnType: ({arr}) => {
    const objectType = arr.objectType;
    const objectTypeTag = Types.isTaggedValue(objectType)
      ? objectType.tag
      : null;
    const strippedObjType = Types.isTaggedValue(objectType)
      ? objectType.value
      : objectType;
    const uTypes = (
      Types.isUnion(strippedObjType)
        ? strippedObjType.members
        : [strippedObjType]
    ).filter(t => {
      return OpTypes.taggableStrip(t) !== 'none';
    });
    const innerType =
      uTypes.length > 0
        ? Types.taggedValue(objectTypeTag, Types.union(uTypes))
        : 'none';
    return Types.list(innerType, 0, Types.listMaxLength(arr));
  },
  resolver: async ({arr}) =>
    arr.filter(x => (Types.isConcreteTaggedValue(x) ? x._value : x) != null),
});

export const opMap = makeListOp({
  name: 'map',
  argTypes: {
    ...listArg0,
    mapFn: {
      type: 'function',
      inputTypes: {row: 'any', index: 'number'},
      outputType: 'any',
    },
  },
  description: `Applies a map function to each element in the ${Graph.docType(
    'list'
  )}`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to map over.`,
    mapFn: `A function to apply to each element of the ${Graph.docType(
      'list'
    )}.`,
  },
  returnValueDescription: `The ${Graph.docType(
    'list'
  )} with each element mapped over.`,
  returnType: ({arr, mapFn}) => {
    if (!Types.isFunction(mapFn)) {
      throw new Error('invalid');
    }
    return Types.list(
      mapFn.outputType,
      Types.listMinLength(arr),
      Types.listMaxLength(arr)
    );
  },
  resolver: async (
    inputs,
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context,
    engine
  ) => {
    const arrType = inputTypes.arr;
    const objectType = Types.listObjectType(arrType);
    const calls: Types.Node[] = unwrapConcreteList(rawInputs.arr).map(
      (x: any, i) =>
        HL.callFunction(inputs.mapFn, {
          row: constNodeUnsafe(objectType, x),
          index: constNumber(i),
        })
    );
    return await engine().executeNodes(calls, false);
  },
  resolveOutputType: async (inputTypes, node, executableNode, client) => {
    // Warning: This assumes that all members in array have the same type!
    // Not a particularly good assumption. We're not really using opMap
    // right now.
    const inputs = node.fromOp.inputs;
    const mapFn = inputs.mapFn;
    const arrType = inputTypes.arr as Types.ListType;
    if (mapFn.nodeType !== 'const') {
      throw new Error('invalid, mapFn must be const');
    }
    // We union the types of everything in the array!

    const runnableNode = replaceInputVariables(
      executableNode.fromOp.inputs.arr
    );
    // We union the types of everything in the array!
    let count = await client.query(opCount({arr: runnableNode}));
    // Put a limit in place for now. We'll expand this later. (25 matches run limit on Weave).
    // TODO: fix
    if (count > MAX_RUN_LIMIT) {
      console.warn('Dropping types in opMap refine');
      count = MAX_RUN_LIMIT;
    }
    const allRefinedProms = _.range(count).map(i =>
      HL.refineNode(client, mapFn.val, {
        row: opIndex({arr: runnableNode, index: constNumber(i)}),
      })
    );
    const allRefined = await Promise.all(allRefinedProms);
    const allUnion =
      count === 0 ? 'none' : Types.union(allRefined.map(n => n.type));
    return {
      type: 'list',
      objectType: allUnion,
      minLength: arrType.minLength,
      maxLength: arrType.maxLength,
    };
  },
});

// Creating a duplicate of opMap which
// uses the new generic mapper that passes
// tags down the line. This will apply the function
// to all elements in a multi-dimensional array (or
// just apply the function to the object if not array)
export const opMapEach = OpKinds.makeTagConsumingStandardOp({
  name: 'mapEach',
  hidden: true,
  argTypes: {
    obj: 'any',
    mapFn: {type: 'function', inputTypes: {row: 'any'}, outputType: 'any'},
  },
  description: `Applies a map function to each "cell" in a multi-dimensional ${Graph.docType(
    'list'
  )}`,
  argDescriptions: {
    obj: `The ${Graph.docType('list')} to map over.`,
    mapFn: `A function to apply to each element of the ${Graph.docType(
      'list'
    )}.`,
  },
  returnValueDescription: `The ${Graph.docType(
    'list'
  )} with each element mapped over.`,
  returnType: ({obj, mapFn}) => {
    if (!Types.isFunction(mapFn)) {
      throw new Error('invalid');
    }
    return mapFn.outputType;
  },
  resolver: async (
    {obj, mapFn},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context,
    engine
  ) =>
    new Promise(async resolve =>
      resolve(
        (
          await engine().executeNodes(
            [
              HL.callFunction(mapFn, {
                row: constNodeUnsafe(inputTypes.obj, obj),
              }),
            ],
            false
          )
        )[0]
      )
    ),
});

function groupKeyObjToString(groupKeyPossibleObj: any): string {
  let groupKey: string = '';
  if (_.isObject(groupKeyPossibleObj)) {
    groupKey = JSON.stringify(Graph.unwrapTaggedValues(groupKeyPossibleObj));
  } else if (groupKeyPossibleObj != null) {
    groupKey = '' + groupKeyPossibleObj;
  }
  return groupKey;
}

export const opGroupby = makeListOp({
  hidden: true,
  name: 'groupby',
  argTypes: {
    arr: {type: 'list', objectType: 'any'},
    groupByFn: {type: 'function', inputTypes: {row: 'any'}, outputType: 'any'},
  },
  description: `Groups elements of a ${Graph.docType(
    'list'
  )} based on a function`,
  argDescriptions: {
    arr: `The ${Graph.docType('list')} to group.`,
    groupByFn: `A function to group the ${Graph.docType('list')} by.`,
  },
  returnValueDescription: `A ${Graph.docType('list')} of ${Graph.docType(
    'list'
  )}, each containing the elements of the original ${Graph.docType(
    'list'
  )} grouped by the function.`,
  returnType: ({arr, groupByFn}) => {
    const objectType = Types.listObjectType(arr);
    if (!Types.isFunction(groupByFn)) {
      throw new Error('invalid group by arg');
    }
    return Types.list(
      Types.withGroupTag(Types.list(objectType), groupByFn.outputType)
    );
  },
  resolver: async (
    inputs,
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context,
    engine
  ) => {
    const tableRows: any[] = inputs.arr;
    // console.log('OP GROUP BY RESOLVER', tableRows.length);
    const groupByFn = inputs.groupByFn;
    const groupSafeKeyFn =
      groupByFn.nodeType === 'void' ? groupByFn : toSafeCall(groupByFn);
    const localEngine = engine();
    const groupRawKeys = await context.trace('groupRawKeys', async () => {
      return (await localEngine.mapNode(groupByFn, tableRows, false)).map(
        (x: any) => x ?? ''
      );
    });
    // console.timeEnd('opGroupBy-rawKeys');

    // console.time('opGroupBy-safeKeys');
    const groupSafeKeys = await context.trace('groupSafeKeys', async () => {
      return (await localEngine.mapNode(groupSafeKeyFn, tableRows, true)).map(
        (x: any) => x ?? ''
      );
    });
    // console.timeEnd('opGroupBy-safeKeys');

    // console.time('opGroupBy-final');
    const result: {[key: string]: {_tag: any; _value: any[]}} = {};
    await context.trace('final', async () => {
      for (let i = 0; i < groupRawKeys.length; i++) {
        const groupRawKey = groupRawKeys[i];
        const groupSafeKey: string = groupKeyObjToString(groupSafeKeys[i]);
        const tableRow = tableRows[i];
        if (result[groupSafeKey] == null) {
          result[groupSafeKey] = Types.concreteWithNamedTag(
            'groupKey',
            groupRawKey,
            []
          );
        }
        result[groupSafeKey]._value.push(tableRow);
      }
    });

    // console.timeEnd('opGroupBy-final');
    return Object.values(result);
  },
  resolveOutputType: async (inputTypes, node, executableNode, context) => {
    // Warning: This assumes that all members in array have the same type!
    // Not a particularly good assumption. We're not really using opMap
    // right now.
    const inputs = node.fromOp.inputs;
    const {arr, groupByFn} = inputs;
    const objectType = inputTypes.arr.objectType;
    if (groupByFn.nodeType !== 'const') {
      throw new Error('invalid, mapFn must be const');
    }
    // return Types.list(
    //   Types.taggedValue(groupByFn.type.outputType, Types.list(objectType))
    // );
    const refined = await HL.refineNode(context, groupByFn.val, {
      row: opIndex({arr, index: constNumber(0)}),
    });
    return Types.list(Types.withGroupTag(Types.list(objectType), refined.type));
  },
});

export const opGroupGroupKey = OpKinds.makeTagGetterOp({
  hidden: true,
  name: 'group-groupkey',
  tagName: 'groupKey',
  tagType: 'any',
});

const listToDict = (
  prev: {[key: string]: number[]},
  curr: any,
  ndx: number
) => {
  if (!prev[curr]) {
    prev[curr] = [];
  }
  prev[curr].push(ndx);
  return prev;
};

// This is an outer join

// This not converted to makeListOp. There are two problems:
// 1. Both arr1 and arr2 need to be nullableTaggable (makeListOp only handles the
//   first argument)
// 2. opJoin returnType enforces that alias1 and alias2 are const nodes.
// TODO: fix
export const opJoin = Graph.makeOp({
  name: 'join',
  argTypes: {
    arr1: Types.maybe({type: 'list', objectType: 'any'}),
    arr2: Types.maybe({type: 'list', objectType: 'any'}),
    join1Fn: {type: 'function', inputTypes: {row: 'any'}, outputType: 'any'},
    join2Fn: {type: 'function', inputTypes: {row: 'any'}, outputType: 'any'},
    alias1: 'string',
    alias2: 'string',
    leftOuter: 'boolean',
    rightOuter: 'boolean',
  },
  description: `Joins two ${Graph.docType('list')}`,
  argDescriptions: {
    arr1: `The first ${Graph.docType('list')} to join.`,
    arr2: `The second ${Graph.docType('list')} to join.`,
    join1Fn: `A function that returns the join key for each element in the first ${Graph.docType(
      'list'
    )}`,
    join2Fn: `A function that returns the join key for each element in the second ${Graph.docType(
      'list'
    )}`,
    alias1: `The alias to use for the first ${Graph.docType('list')}.`,
    alias2: `The alias to use for the second ${Graph.docType('list')}.`,
    leftOuter: `Whether the output should include rows in the first ${Graph.docType(
      'list'
    )} that do not match any rows in the second ${Graph.docType('list')}.`,
    rightOuter: `Whether the output should include rows in the second ${Graph.docType(
      'list'
    )} that do not match any rows in the first ${Graph.docType('list')}.`,
  },
  returnValueDescription: `A ${Graph.docType('list')} of ${Graph.docType(
    'typedDict'
  )}. Each ${Graph.docType(
    'typedDict'
  )} has keys \`alias1\` and \`alias2\`. The values are the rows corresponding to the join.`,
  returnType: inputs => {
    const {arr1, arr2, alias1, alias2, join1Fn, join2Fn} = inputs;
    if (alias1.nodeType !== 'const' || alias2.nodeType !== 'const') {
      // TODO:
      //   this needs to be resolved. Need to refactor to allow async
      //   ops, while still retaining ability to one-shot entire graph.
      throw new Error('opJoin only supports const aliases');
    }
    const arr1ObjectType = Types.listObjectType(arr1.type);
    const arr2ObjectType = Types.listObjectType(arr2.type);
    return Types.list(
      Types.withJoinTag(
        Types.typedDict({
          [alias1.val]: Types.maybe(arr1ObjectType),
          [alias2.val]: Types.maybe(arr2ObjectType),
        }),
        Types.maybe(
          Types.union([join1Fn.type.outputType, join2Fn.type.outputType])
        )
      )
    );
  },
  resolver: async (inputs, forwardGraph, forwardOp, context, engine) => {
    const {join1Fn, join2Fn, alias1, alias2, leftOuter, rightOuter} = inputs;
    let arr1 = OpTypes.getValueFromTaggedValue(inputs.arr1);
    let arr2 = OpTypes.getValueFromTaggedValue(inputs.arr2);
    const arr1RowType = Types.listObjectType(
      forwardOp.op.inputs.arr1.type as Types.ListType
    );
    const arr2RowType = Types.listObjectType(
      forwardOp.op.inputs.arr1.type as Types.ListType
    );

    if (arr1 == null) {
      arr1 = [];
    }
    if (arr2 == null) {
      arr2 = [];
    }

    let calls: Types.Node[] = arr1.map((x: any) =>
      HL.callFunction(join1Fn, {
        row: constNodeUnsafe(arr1RowType, x),
      })
    );
    calls = calls
      .concat(
        arr2.map((x: any) =>
          HL.callFunction(join2Fn, {
            row: constNodeUnsafe(arr2RowType, x),
          })
        )
      )
      .map(toSafeCall);

    const joinKeys = (await engine().executeNodes(calls, true)).map(
      groupKeyObjToString
    );
    const arr1JoinKeys = joinKeys.slice(0, arr1.length);
    const arr2JoinKeys = joinKeys.slice(arr1.length);

    const arr1JoinKeyLookup = arr1JoinKeys.reduce(listToDict, {});
    const arr2JoinKeyLookup = arr2JoinKeys.reduce(listToDict, {});
    let allJoinKeys: any[];
    if (leftOuter && rightOuter) {
      allJoinKeys = _.union(arr1JoinKeys, arr2JoinKeys);
    } else if (leftOuter) {
      allJoinKeys = _.uniq(arr1JoinKeys);
    } else if (rightOuter) {
      allJoinKeys = _.uniq(arr2JoinKeys);
    } else {
      allJoinKeys = _.intersection(arr1JoinKeys, arr2JoinKeys);
    }
    const result: any[] = [];
    for (const jk of allJoinKeys) {
      let arr1Indexes: Array<number | null> = arr1JoinKeyLookup[jk];
      let arr2Indexes: Array<number | null> = arr2JoinKeyLookup[jk];
      if (rightOuter && !arr1Indexes) {
        arr1Indexes = [null];
      }
      if (leftOuter && !arr2Indexes) {
        arr2Indexes = [null];
      }
      for (const arr1Index of arr1Indexes) {
        for (const arr2Index of arr2Indexes) {
          result.push(
            Types.concreteTaggedValue(
              {joinKey: jk},
              {
                [alias1]: arr1Index != null ? arr1[arr1Index!] : null,
                [alias2]: arr2Index != null ? arr2[arr2Index!] : null,
              }
            )
          );
        }
      }
    }
    return result;
  },
  resolveOutputType: async (node, executableNode, context) => {
    const {arr1, arr2, alias1, alias2, join1Fn, join2Fn} = node.fromOp.inputs;
    if (
      alias1.nodeType !== 'const' ||
      alias2.nodeType !== 'const' ||
      join1Fn.nodeType !== 'const' ||
      join2Fn.nodeType !== 'const'
    ) {
      // TODO:
      //   this needs to be resolved. Need to refactor to allow async
      //   ops, while still retaining ability to one-shot entire graph.
      throw new Error('opJoin only supports const aliases & functions');
    }
    const arr1Refined = await HL.refineNode(context, arr1, {});
    const arr2Refined = await HL.refineNode(context, arr2, {});
    const join1FnValRefined = await HL.updateFunctionForInputs(
      context,
      join1Fn.val,
      {row: opIndex({arr: arr1Refined, index: constNumber(0)})}
    );
    const join2FnValRefined = await HL.updateFunctionForInputs(
      context,
      join2Fn.val,
      {row: opIndex({arr: arr2Refined, index: constNumber(0)})}
    );
    const arr1ObjectType = Types.listObjectType(arr1Refined.type);
    const arr2ObjectType = Types.listObjectType(arr2Refined.type);
    return {
      ...node,
      type: Types.list(
        Types.withJoinTag(
          Types.typedDict({
            [alias1.val]: Types.maybe(arr1ObjectType),
            [alias2.val]: Types.maybe(arr2ObjectType),
          }),
          Types.maybe(
            Types.union([join1FnValRefined.type, join2FnValRefined.type])
          )
        )
      ),
    };
  },
});

const typedDictWithLeafsAsLists = (
  type: Types.Type,
  maxLength: number | undefined,
  tag?: Types.Type
): Types.Type => {
  return OpTypes.nullableSkipTaggable(type, (t, tagType) => {
    if (!Types.isTypedDictLike(t)) {
      throw new Error('invalid');
    }
    return Types.typedDict(
      _.mapValues(Types.typedDictPropertyTypes(t), propType =>
        Types.isTypedDictLike(Types.nullableTaggableValue(propType!))
          ? typedDictWithLeafsAsLists(
              propType!,
              maxLength,
              // If we unwrapped a tag
              tagType != null
                ? tag != null
                  ? // pass it down tagged with the current tag
                    Types.taggedValue(tag, tagType)
                  : tagType
                : // otherwise just pass tag through
                  tag
            )
          : Types.list(
              OpTypes.nullableTaggable(
                propType!,
                unwrappedPropType => unwrappedPropType
              ),
              undefined,
              maxLength
            )
      )
    );
  });
};

const typedDictWithListProperties = (
  type: Types.Type,
  maxLength: number | undefined,
  tag?: Types.Type
): Types.Type => {
  return OpTypes.skipTaggable(type, (t, tagType) => {
    if (!Types.isTypedDictLike(t)) {
      throw new Error('invalid');
    }
    return Types.typedDict(
      _.mapValues(Types.typedDictPropertyTypes(t), propType =>
        Types.list(propType, undefined, maxLength)
      )
    );
  });
};

const objectPathWithTagsPushedToLeafs = (obj: any, path: string[]): any => {
  return OpTypes.nullableTaggableVal(obj, v =>
    path.length === 1
      ? v[path[0]]
      : objectPathWithTagsPushedToLeafs(v[path[0]], path.slice(1))
  );
};

const addItemAtPath = (
  obj: {[key: string]: any},
  path: string[],
  val: any
): any => {
  let writeTo = obj;
  for (const entry of path.slice(0, path.length - 1)) {
    if (writeTo[entry] == null) {
      writeTo[entry] = {};
    }
    writeTo = writeTo[entry];
  }
  const lastEntry = path[path.length - 1];
  if (writeTo[lastEntry] == null) {
    writeTo[lastEntry] = [];
  }
  if (!_.isArray(writeTo[lastEntry])) {
    // TODO: we need to handle this case (path collision). Need to handle
    // it at the type level as well
    throw new Error('uh-ohhhhhh');
  }
  writeTo[lastEntry].push(val);
};

// Lodash's cloneDeep is super slow, this is way better
const cloneDeepObj = (obj: {[key: string]: any}): any => {
  if (_.isArray(obj)) {
    return [...obj];
  }
  if (!_.isObject(obj)) {
    return obj;
  }
  return _.mapValues(obj, cloneDeepObj);
};

export const opJoinAll = Graph.makeOp({
  hidden: true,
  name: 'joinAll',
  argTypes: {
    arrs: {
      type: 'list',
      objectType: {
        type: 'union',
        members: [
          'none',
          {
            type: 'list',
            objectType: {type: 'typedDict', propertyTypes: {}},
          },
        ],
      },
    },
    joinFn: {
      type: 'function',
      inputTypes: {row: {type: 'typedDict', propertyTypes: {}}},
      outputType: 'any',
    },
    outer: 'boolean',
  },
  description: `Join all ${Graph.docType('list')} together`,
  argDescriptions: {
    arrs: `${Graph.docType('list')} to join`,
    joinFn: `Function to calculate join key`,
    outer: `Whether to include rows from ${Graph.docType(
      'list'
    )} which do not match others`,
  },
  returnValueDescription: `A ${Graph.docType('list')} of ${Graph.docType(
    'typedDict',
    {plural: true}
  )}. Each dictionary contains keys 0 - N, where N is the count of ${Graph.docType(
    'list'
  )}.`,
  returnType: inputs => {
    const {arrs, joinFn} = inputs;

    if (!Types.isFunction(joinFn.type)) {
      throw new Error('invalid');
    }
    return opJoinAllReturnType(arrs.type, joinFn.type);
  },
  resolver: async (inputs, forwardGraph, forwardOp, context, engine) => {
    const {arrs, joinFn, outer} = inputs;
    const arrsUntagged = OpTypes.getValueFromTaggedValue(arrs)
      .map(OpTypes.getValueFromTaggedValue)
      .filter((arr: any) => arr != null);
    const allColumns = allObjPaths(
      Types.listObjectType(Types.listObjectType(forwardOp.op.inputs.arrs.type)),
      0
    ).map(pt => pt.path);

    const joinFnCalls: Types.Node[] = _.flatMap(
      arrsUntagged,
      (arr, ndx, collection) =>
        arr.map((x: any) =>
          HL.callFunction(joinFn, {
            row: constNodeUnsafe({type: 'typedDict', propertyTypes: {}}, x),
          })
        )
    );
    const safeJoinFnCalls = joinFnCalls.map(toSafeCall);

    const localEngine = engine();

    const joinObjsMerged = await localEngine.executeNodes(joinFnCalls, false);

    const joinKeysMerged = (
      await localEngine.executeNodes(safeJoinFnCalls, true)
    ).map(groupKeyObjToString);

    const joinKeysToObjs = _.fromPairs(
      _.zip(joinKeysMerged, joinObjsMerged).filter(pair => pair[1] != null)
    );

    let pos = 0;
    const joinKeys: string[][] = arrsUntagged.map((val: any[]) => {
      const ret = joinKeysMerged.slice(pos, pos + val.length);
      pos += val.length;
      return ret;
    });

    const joinKeyLookups = joinKeys.map(jk => jk.reduce(listToDict, {}));

    const allJoinKeys = outer
      ? _.union(...joinKeys)
      : _.intersection(...joinKeys);

    const result: any[] = [];
    for (const jk of allJoinKeys) {
      // do not join null values
      if (jk !== '') {
        let newRows: Array<{[column: string]: any[]}> = [];

        joinKeyLookups.forEach((jkl, ndx) => {
          const arrIndexes = jkl[jk] ?? [];
          if (newRows.length === 0) {
            arrIndexes.forEach((arrIndex: number) => {
              const newRow: {[column: string]: any} = {};
              const arrItem = arrsUntagged[ndx][arrIndex!];
              allColumns.forEach(colPath => {
                const val = objectPathWithTagsPushedToLeafs(arrItem, colPath);
                addItemAtPath(newRow, colPath, val);
              });
              newRows.push(newRow);
            });
            if (arrIndexes.length === 0 && outer) {
              const newRow: {[column: string]: any} = {};
              const arrItem = null;
              allColumns.forEach(colPath => {
                const val = objectPathWithTagsPushedToLeafs(arrItem, colPath);
                addItemAtPath(newRow, colPath, val);
              });
              newRows.push(newRow);
            }
          } else {
            const newRowsCopy: Array<{[column: string]: any[]}> = [];
            newRows.forEach(oldRow => {
              arrIndexes.forEach((arrIndex: number) => {
                const newRow: {[column: string]: any[]} = cloneDeepObj(oldRow);
                const arrItem = arrsUntagged[ndx][arrIndex!];
                allColumns.forEach(colPath => {
                  const val = objectPathWithTagsPushedToLeafs(arrItem, colPath);
                  addItemAtPath(newRow, colPath, val);
                });
                newRowsCopy.push(newRow);
              });
              if (arrIndexes.length === 0 && outer) {
                const newRow: {[column: string]: any[]} = cloneDeepObj(oldRow);
                const arrItem = null;
                allColumns.forEach(colPath => {
                  const val = objectPathWithTagsPushedToLeafs(arrItem, colPath);
                  addItemAtPath(newRow, colPath, val);
                });
                newRowsCopy.push(newRow);
              }
            });
            newRows = newRowsCopy;
          }
        });
        newRows.forEach(row => {
          result.push(
            Types.concreteTaggedValue(
              {joinKey: jk, joinObj: joinKeysToObjs[jk]},
              row
            )
          );
        });
      }
    }

    return result;
  },
  resolveOutputType: async (node, executableNode, client) => {
    // Same as returnType, except we get the length of input array
    // and set that as the maxLength of the arrays we return at
    // each key
    const count = await client.query(
      opCount({arr: executableNode.fromOp.inputs.arrs})
    );
    const inputs = node.fromOp.inputs;
    const type = opJoinAllReturnType(
      inputs.arrs.type,
      inputs.joinFn.type,
      count
    );
    return {...node, type};
  },
});

export const opGetJoinedJoinObj = OpKinds.makeTagGetterOp({
  hidden: true,
  name: 'tag-joinObj',
  tagName: 'joinObj',
  tagType: 'any',
});

// TODO: opConcat doesn't handle nullable taggable cases yet.
const opConcatArgs = {
  arr: {
    type: 'list' as const,
    objectType: {
      type: 'union' as const,
      members: [
        'none' as const,
        {type: 'list' as const, objectType: 'any' as const},
      ],
    },
  },
};

export const opConcat = OpKinds.makeBasicOp<typeof opConcatArgs>({
  name: 'concat',
  argTypes: opConcatArgs,
  description: `Concatenate all ${Graph.docType('list', {
    plural: true,
  })} together`,
  argDescriptions: {
    arr: `${Graph.docType('list')} of ${Graph.docType('list', {
      plural: true,
    })} to concatenate`,
  },
  returnValueDescription: `Concatenated ${Graph.docType('list')}`,
  returnType: ({arr}) => {
    const innerType = Types.nonNullable(arr.objectType);
    // If you are concatenating lists of different types (eg. concat tables with
    // different columns), then the resulting list needs to be
    // a union of the inner types of those lists
    if (Types.isUnion(innerType)) {
      if (!innerType.members.every(Types.isListLike)) {
        throw new Error('invalid');
      }
      return Types.list(
        Types.union(innerType.members.map(Types.listObjectType))
      );
    } else if (Types.isTaggedValue(innerType)) {
      const innerTypeValue = Types.taggedValueValueType(innerType);
      const innerTypeTag = Types.taggedValueTagType(innerType);
      const innerTypeValueInnerType = Types.listObjectType(innerTypeValue);

      return Types.list(
        Types.taggedValue(innerTypeTag, innerTypeValueInnerType)
      );
    }
    return innerType;
  },
  resolver: async inputs => {
    return _.concat(
      [],
      ...inputs.arr
        .map(val => {
          // Should we have a generic way to do this in situations where we
          // need to preserve the tag of the outer array?
          if (Types.isConcreteTaggedValue(val) && _.isArray(val._value)) {
            return val._value.map(v => Types.concreteTaggedValue(val._tag, v));
          }
          return val;
        })
        .filter((item: any) => item != null)
    );
  },
});

// Not yet public
// TODO: This doesn't properly handle tagged values in the types
export const opUnnest = Graph.makeOp({
  hidden: true,
  name: 'unnest',
  argTypes: {
    arr: {type: 'list', objectType: {type: 'typedDict', propertyTypes: {}}},
  },
  description: `Unnest a ${Graph.docType('list')} of ${Graph.docType(
    'typedDict'
  )} such that the result is a ${Graph.docType('list')} of ${Graph.docType(
    'typedDict'
  )} where each ${Graph.docType('typedDict')}'s properties are non-lists.`,
  argDescriptions: {
    arr: `${Graph.docType('list')} of ${Graph.docType('typedDict')} to unnest`,
  },
  returnValueDescription: `Unnested ${Graph.docType('list')}`,
  returnType: inputs => {
    const {arr} = inputs;
    const objType = Types.listObjectType(arr.type);
    if (!Types.isTypedDictLike(objType)) {
      throw new Error('invalid');
    }
    const newPropertyTypes: {[key: string]: Types.Type} = {};
    const propTypes = Types.typedDictPropertyTypes(objType);
    for (const key of Object.keys(propTypes)) {
      const propertyType = propTypes[key];
      if (propertyType == null) {
        throw new Error('invalid');
      }
      newPropertyTypes[key] = Types.isList(propertyType)
        ? propertyType.objectType
        : propertyType;
    }
    return {
      type: 'list',
      objectType: {type: 'typedDict', propertyTypes: newPropertyTypes},
    };
  },
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const arrNode = forwardOp.outputNode.node.fromOp.inputs.arr;
    const objType = Types.listObjectType(arrNode.type);
    const keys: string[] = [];
    const propTypes = Types.typedDictPropertyTypes(objType);
    for (const key of Object.keys(propTypes)) {
      const propertyType = propTypes[key];
      if (Types.isListLike(propertyType)) {
        keys.push(key);
      }
    }

    // getValueFromTaggedValue is wrong, should retain tags
    // TODO
    const arr: Array<{[key: string]: any}> = OpTypes.getValueFromTaggedValue(
      inputs.arr
    );

    const result: Array<{[key: string]: any}> = [];
    for (const row of arr) {
      const distributedRow: {[key: string]: any} = {};
      for (const key of Object.keys(row)) {
        const val = row[key];
        if (!keys.includes(key)) {
          distributedRow[key] = val;
        }
      }
      if (keys.length > 0) {
        const arraysToUnnest: any[] = [];
        for (const key of keys) {
          const innerArr = row[key];
          // Distributed tags over array
          if (Types.isConcreteTaggedValue(innerArr)) {
            arraysToUnnest.push(
              (innerArr._value as any)?.map((innerR: any) =>
                Types.concreteTaggedValue(innerArr._tag, innerR)
              )
            );
          } else {
            arraysToUnnest.push(innerArr);
          }
        }
        for (const zipped of _.zip(...arraysToUnnest)) {
          const newRow = {...distributedRow};
          for (const [i, key] of keys.entries()) {
            newRow[key] = zipped[i];
          }
          result.push(newRow);
        }
      } else {
        result.push(distributedRow);
      }
    }
    return result;
  },
});

export const opContains = makeListOp({
  name: 'contains',
  argTypes: {arr: {type: 'list', objectType: 'string'}, element: 'string'},
  description: `Check if a ${Graph.docType('list')} contains a ${Graph.docType(
    'string'
  )}`,
  argDescriptions: {
    arr: `${Graph.docType('list')} to check`,
    element: `${Graph.docType('string')} to check for`,
  },
  returnValueDescription: `True if the ${Graph.docType(
    'list'
  )} contains the ${Graph.docType('string')}`,
  returnType: inputTypes => 'boolean',
  resolver: inputs => {
    const {arr} = inputs;
    let {element} = inputs;
    // TODO(np): Hack around element coming in as tagged value
    if (Types.isConcreteTaggedValue(element)) {
      element = Graph.unwrapTaggedValues(element);
    }
    return arr.indexOf(element) !== -1;
  },
});

const concreteTagSafeFlatten = (arrOrObj: any, parentTag?: any): any => {
  if (Types.isConcreteTaggedValue(arrOrObj)) {
    const arrOrObjTag = OpTypes.getTagFromTaggedValue(arrOrObj);
    if (parentTag != null && arrOrObjTag != null) {
      parentTag = Types.concreteTaggedValue(parentTag, arrOrObjTag);
    } else if (arrOrObjTag != null) {
      parentTag = arrOrObjTag;
    }
    arrOrObj = OpTypes.getValueFromTaggedValue(arrOrObj);
  }

  if (!_.isArray(arrOrObj)) {
    if (parentTag != null) {
      return Types.concreteTaggedValue(parentTag, arrOrObj);
    } else {
      return arrOrObj;
    }
  } else {
    return _.flatMap(arrOrObj, item => concreteTagSafeFlatten(item, parentTag));
  }
};

const tagSafeFlatten = (
  arrOrObj: Types.Type,
  parentTag?: Types.Type
): Types.Type => {
  if (Types.isTaggedValue(arrOrObj)) {
    const arrOrObjTag = arrOrObj.tag;
    if (parentTag != null && arrOrObjTag != null) {
      parentTag = Types.taggedValue(parentTag, arrOrObjTag);
    } else if (arrOrObjTag != null) {
      parentTag = arrOrObjTag;
    }
    arrOrObj = arrOrObj.value;
  }

  if (!Types.isListLike(arrOrObj)) {
    if (parentTag != null) {
      return Types.taggedValue(parentTag, arrOrObj);
    } else {
      return arrOrObj;
    }
  } else {
    return tagSafeFlatten(Types.listObjectType(arrOrObj), parentTag);
  }
};

// TODO: convert to makeListOp
export const opFlatten = Graph.makeOp({
  hidden: true,
  name: 'flatten',
  argTypes: {arr: {type: 'list', objectType: 'any'}},
  description: `Flatten a ${Graph.docType('list')} of ${Graph.docType('list', {
    plural: true,
  })}`,
  argDescriptions: {
    arr: `${Graph.docType('list')} of ${Graph.docType('list', {
      plural: true,
    })} to flatten`,
  },
  returnValueDescription: `Flattened ${Graph.docType('list')}`,
  returnType: inputTypes => Types.list(tagSafeFlatten(inputTypes.arr.type)),
  resolver: inputs => concreteTagSafeFlatten(inputs.arr),
});
function opJoinAllReturnType(
  arrsType: {
    type: 'list';
    objectType: {
      type: 'union';
      members: Array<
        | 'none'
        | {type: 'list'; objectType: {type: 'typedDict'; propertyTypes: {}}}
      >;
    };
  },
  joinFnType: {
    type: 'function';
    inputTypes: {row: {type: 'typedDict'; propertyTypes: {}}};
    outputType: 'any';
  },
  count?: number
): Types.Type {
  return OpTypes.nullableTaggable(arrsType, outerListType => {
    if (!Types.isList(outerListType)) {
      throw new Error('invalid');
    }
    return Types.list(
      Types.withJoinTag(
        // Skip nulls here, we only want them inside the arrays at the keys.
        OpTypes.skipNullableTaggable(
          outerListType.objectType,
          innerListType => {
            if (!Types.isListLike(innerListType)) {
              throw new Error('invalid');
            }
            // Skip nulls here, we only want them inside the arrays at the keys
            return OpTypes.skipNullableTaggable(
              Types.listObjectType(innerListType),
              objType => {
                if (!Types.isTypedDictLike(objType)) {
                  throw new Error('invalid');
                }
                return typedDictWithListProperties(objType, count);
              }
            );
          }
        ),
        joinFnType.outputType
      )
    );
  });
}

const projectableType = {
  type: 'list' as const,
  objectType: Types.maybe({
    type: 'list' as const,
    objectType: Types.maybe('number' as const),
  }),
};
const projectableToRawList = (input: any[]): any[][] => {
  return input.map(i =>
    OpTypes.getValueFromTaggedValue(i).map(OpTypes.getValueFromTaggedValue)
  );
};

const ensureNDimensions = (
  input: any[][],
  n: number,
  nLimit: number
): any[][] => {
  n = Math.min(n, nLimit);
  return input.map(row => {
    if (row == null) {
      row = [];
    }
    if (row.length < n) {
      return row.concat(Array(n - row.length).fill(0));
    } else if (row.length === n) {
      return row;
    } else {
      return row.slice(0, nLimit);
    }
  });
};

const wrapConcreteNewListElementsWithOldListElementTags = (
  newArr: any[],
  oldArr: any[]
) => {
  if (newArr.length !== oldArr.length) {
    throw new Error('Expected both arrays to be of the same length');
  }
  return newArr.map((newElem, i) => {
    const oldElem = oldArr[i];
    if (Types.isConcreteTaggedValue(oldElem)) {
      return Types.concreteTaggedValue(
        OpTypes.getTagFromTaggedValue(oldElem),
        newElem
      );
    } else {
      return newElem;
    }
  });
};

const replaceListElementType = (
  newElementType: Types.Type,
  sourceListType: Types.ListType
): Types.ListType => {
  const innerType = Types.listObjectType(sourceListType);
  const minLength = Types.listMinLength(sourceListType);
  const maxLength = Types.listMaxLength(sourceListType);
  if (Types.isTaggedValue(innerType)) {
    newElementType = Types.taggedValue(
      Types.taggedValueTagType(innerType),
      newElementType
    );
  }
  return Types.list(newElementType, minLength, maxLength);
};

export const opListTSNE = OpKinds.makeConfigurableStandardOp({
  name: 'list-tSNE',
  hidden: true,
  argTypes: {
    arr: Types.maybe(projectableType),
    dimensions: 'number',
    perplexity: 'number',
    learningRate: 'number',
    iterations: 'number',
  },
  typeConfig: {
    dims: 2,
  },
  description: `Run t-SNE on a ${Graph.docType('list')} of ${Graph.docType(
    'list',
    {plural: true}
  )} of numbers (vectors)`,
  argDescriptions: {
    arr: `List of ${Graph.docType('list', {plural: true})} to run t-SNE on`,
    dimensions: 'Number of dimensions to reduce to',
    perplexity: 'Perplexity of the t-SNE algorithm',
    learningRate: 'Learning rate of the t-SNE algorithm',
    iterations: 'Number of iterations of the t-SNE algorithm',
  },
  returnValueDescription: `A t-SNE projected ${Graph.docType(
    'list'
  )} of ${Graph.docType('list', {plural: true})} of numbers (vectors).`,
  returnType: ({arr}) => {
    return replaceListElementType(Types.list('number'), arr as Types.ListType);
  },
  resolver: async ({arr, dimensions, perplexity, learningRate, iterations}) => {
    const data = ensureNDimensions(
      projectableToRawList(arr),
      dimensions,
      MAX_PROJECTION_DIMENSIONS
    );
    const tsne = new tSNE({
      epsilon: learningRate,
      perplexity,
      dim: dimensions,
    });
    tsne.initDataRaw(data);
    for (let k = 0; k < iterations; k++) {
      tsne.step();
    }
    return wrapConcreteNewListElementsWithOldListElementTags(
      tsne.getSolution(),
      arr
    );
  },
});

export const opListUMAP = OpKinds.makeConfigurableStandardOp({
  name: 'list-UMAP',
  hidden: true,
  argTypes: {
    arr: Types.maybe(projectableType),
    dimensions: 'number',
    neighbors: 'number',
    minDist: 'number',
    spread: 'number',
  },
  typeConfig: {
    dims: 2,
  },
  description: `Run UMAP on a ${Graph.docType('list')} of ${Graph.docType(
    'list',
    {plural: true}
  )} of numbers (vectors)`,
  argDescriptions: {
    arr: `List of ${Graph.docType('list', {plural: true})} to run UMAP on`,
    dimensions: 'Number of dimensions to reduce to',
    neighbors: 'Number of neighbors to use',
    minDist: 'Minimum distance between neighbors',
    spread: 'Spread of the neighborhood',
  },
  returnValueDescription: `A UMAP projected ${Graph.docType(
    'list'
  )} of ${Graph.docType('list', {plural: true})} of numbers (vectors)`,
  returnType: ({arr}) => {
    return replaceListElementType(Types.list('number'), arr as Types.ListType);
  },
  resolver: async ({arr, dimensions, neighbors, minDist, spread}) => {
    const {UMAP} = await import('umap-js');
    const data = ensureNDimensions(
      projectableToRawList(arr),
      dimensions,
      MAX_PROJECTION_DIMENSIONS
    );
    const umap = new UMAP({
      nComponents: dimensions,
      // Just let the algorithm decide automatically
      // nEpochs: epochs,
      nNeighbors: neighbors,
      minDist,
      spread,
    });
    return wrapConcreteNewListElementsWithOldListElementTags(
      umap.fit(data),
      arr
    );
  },
});

// tslint:disable-next-line: ban-types
const withDisabledConsoleLog = <FT extends Function>(fn: FT) => {
  const log = console.log;
  console.log = () => {};
  const res = fn();
  console.log = log;
  return res;
};
export const opListPCA = OpKinds.makeConfigurableStandardOp({
  name: 'list-PCA',
  hidden: true,
  argTypes: {
    arr: Types.maybe(projectableType),
    dimensions: 'number',
  },
  typeConfig: {
    dims: 2,
  },
  description: `Run PCA on a ${Graph.docType('list')} of ${Graph.docType(
    'list',
    {plural: true}
  )} of numbers (vectors)`,
  argDescriptions: {
    arr: `List of ${Graph.docType('list', {plural: true})} to run PCA on`,
    dimensions: 'Number of dimensions to reduce to',
  },
  returnValueDescription: `A PCA projected ${Graph.docType(
    'list'
  )} of ${Graph.docType('list', {plural: true})} of numbers (vectors)`,
  returnType: ({arr}) => {
    return replaceListElementType(Types.list('number'), arr as Types.ListType);
  },
  resolver: async ({arr, dimensions}) => {
    const PCA = await import('pca-js');
    const data = ensureNDimensions(
      projectableToRawList(arr),
      dimensions,
      MAX_PROJECTION_DIMENSIONS
    );
    const vectors: any[] = withDisabledConsoleLog(() =>
      PCA.getEigenVectors(data)
    );
    return wrapConcreteNewListElementsWithOldListElementTags(
      PCA.transpose(
        PCA.computeAdjustedData(data, ...vectors.slice(0, dimensions))
          .adjustedData
      ),
      arr
    );
  },
});

// This is used by the projection converter. Should not be exposed. This is a good
// use case to understand the how we can work towards a more general approach to op
// configs as the arg types are purposely the same as the converter.

const MAX_PROJECTION_RECORDS = 1500;
const MAX_PROJECTION_DIMENSIONS = 50;
export const opTable2DProjection = OpKinds.makeConfigurableStandardOp({
  name: 'table-2DProjection',
  hidden: true,
  typeConfig: {
    dims: 1,
  },
  argTypes: {
    table: {
      type: 'list' as const,
      objectType: Types.maybe({type: 'typedDict' as const, propertyTypes: {}}),
    },
    projectionAlgorithm: 'string',
    inputCardinality: 'string',
    inputColumnNames: {type: 'list' as const, objectType: 'string'},
    algorithmOptions: {
      type: 'typedDict',
      propertyTypes: {
        tsne: {
          type: 'typedDict' as const,
          propertyTypes: {
            perplexity: 'number',
            learningRate: 'number',
            iterations: 'number',
          },
        },
        pca: {type: 'typedDict' as const, propertyTypes: {}},
        umap: {
          type: 'typedDict' as const,
          propertyTypes: {
            neighbors: 'number',
            minDist: 'number',
            spread: 'number',
          },
        },
      },
    },
  },
  description: `Apply a 2D projection to a ${Graph.docType(
    'list'
  )} of ${Graph.docType('typedDict', {plural: true})}`,
  argDescriptions: {
    table: `${Graph.docType('list')} of ${Graph.docType('typedDict', {
      plural: true,
    })} to project`,
    projectionAlgorithm: 'Algorithm to use (pca, tsne, umap)',
    inputCardinality: 'Cardinality of the input data (single, multiple)',
    inputColumnNames: 'List of column names to project',
    algorithmOptions: 'Options for the algorithm',
  },
  returnValueDescription: `${Graph.docType('list')} of ${Graph.docType(
    'typedDict',
    {plural: true}
  )} with projected columns`,
  returnType: ({table}) => {
    return replaceListElementType(
      Types.typedDict({
        projection: Types.typedDict({
          x: 'number',
          y: 'number',
        }),
        source: Types.listObjectType(table),
      }),
      table as Types.ListType
    );
  },
  resolver: async (
    {
      table,
      inputCardinality,
      inputColumnNames,
      algorithmOptions,
      projectionAlgorithm,
    },
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context,
    engine
  ) => {
    table = _.sampleSize(table, MAX_PROJECTION_RECORDS);

    if (inputColumnNames.length === 0) {
      return wrapConcreteNewListElementsWithOldListElementTags(
        table.map((row, ndx) => ({
          projection: {
            x: 0,
            y: 0,
          },
          source: table[ndx],
        })),
        table
      );
    }

    if (inputCardinality === 'single') {
      inputColumnNames = [inputColumnNames[0]];
    } else {
      // Artificially constrain the size of the projection to a reasonable number.
      // This can likely be lifted if/when use case demands it and we make a more performant
      // backend
      inputColumnNames = _.sampleSize(
        inputColumnNames,
        MAX_PROJECTION_DIMENSIONS
      );
    }

    const embeddings = table.map(row => {
      if (inputCardinality === 'single') {
        return OpTypes.typedDictPathVal(
          OpTypes.getValueFromTaggedValue(row),
          splitEscapedString(inputColumnNames[0])
        );
      } else {
        return inputColumnNames.map(col =>
          OpTypes.typedDictPathVal(
            OpTypes.getValueFromTaggedValue(row),
            splitEscapedString(col)
          )
        );
      }
    });

    const embeddingNode = constNode<typeof projectableType>(
      projectableType,
      embeddings
    );
    const castedAlgorithmOptions = algorithmOptions as any;
    let projectionOp: Types.Node;
    if (projectionAlgorithm === 'pca') {
      projectionOp = opListPCA({
        arr: embeddingNode,
        dimensions: constNumber(2),
      });
    } else if (projectionAlgorithm === 'tsne') {
      projectionOp = opListTSNE({
        arr: embeddingNode,
        dimensions: constNumber(2),
        perplexity: constNumber(castedAlgorithmOptions.tsne.perplexity),
        learningRate: constNumber(castedAlgorithmOptions.tsne.learningRate),
        iterations: constNumber(castedAlgorithmOptions.tsne.iterations),
      });
    } else if (projectionAlgorithm === 'umap') {
      projectionOp = opListUMAP({
        arr: embeddingNode,
        dimensions: constNumber(2),
        neighbors: constNumber(castedAlgorithmOptions.umap.neighbors),
        minDist: constNumber(castedAlgorithmOptions.umap.minDist),
        spread: constNumber(castedAlgorithmOptions.umap.spread),
      });
    } else {
      throw new Error('Invalid projectionAlgorithm');
    }

    const projections = (await engine().executeNodes([projectionOp]))[0];
    const res = projections.map((p: number[], ndx: number) => ({
      projection: {
        x: p[0],
        y: p[1],
      },
      source: table[ndx],
    }));

    return wrapConcreteNewListElementsWithOldListElementTags(res, table);
  },
});
